import React, { useEffect, useState } from 'react';
import { Footer } from '@appsvio/config-page-layout';
import { ErrorMessage } from '@atlaskit/form';
import Spinner from '@atlaskit/spinner';
import Textfield from '@atlaskit/textfield';
import { router, view } from '@forge/bridge';
import { FullContext } from '@forge/bridge/out/types';

import { DOCS_URL } from '../../../common/constants';
import { ABSOLUTE } from '../../../components/config/constants';
import { FieldConfigContent, PositionedElement } from '../../../components/config/styles';
import { useIsLicenseActive } from '../../../hooks/useIsLicenseActive';
import { LicenseBanner } from '../../../utils/LicenseBanner';
import { InfoText, Label } from '../../Styled';

import LoadTemplate from './components/LoadTemplate';
import TestingArea from './components/TestingArea';
import { ContentWrapper, PatternSection, Wrapper } from './indexStyled';
import { regexExamples } from './regexExamples';

const REGEX_CHEATSHEET_URL = 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_expressions/Cheatsheet';

type FieldConfig = {
    error: string;
    regex: string;
};

const Configuration: React.FC = () => {
    const [regexPattern, setRegexPattern] = useState('');
    const [regexErrorMessage, setRegexErrorMessage] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [isFormInvalid, setIsFormInvalid] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { isLicenseActive } = useIsLicenseActive();

    const handleRegexTemplateChange = (regex: string, templateName: string) => {
        setRegexPattern(regex);
        setIsFormInvalid(false);
        setSelectedTemplate(templateName);
    };

    const determineConfiguredTemplateName = (regex: string) => {
        const matchedTemplate = regexExamples.find(exampleItem => exampleItem.regex === regex);

        matchedTemplate && setSelectedTemplate(matchedTemplate.label);
    };

    const setConfigRelatedData = (context: FullContext) => {
        if (context.extension.configuration) {
            const configuration = context.extension.configuration as FieldConfig;
            setRegexPattern(configuration.regex);
            setRegexErrorMessage(configuration.error);
            determineConfiguredTemplateName(configuration.regex);
        }
    };

    const handleCancel = async () => {
        const context = await view.getContext();
        setConfigRelatedData(context);
    };

    const removeFirstAndLastSlash = (str: string) => str.replace(/^\/|\/$/g, '');

    const handleSubmit = async () => {
        setIsLoading(true);
        setIsFormInvalid(false);

        if (regexPattern) {
            const config = { regex: removeFirstAndLastSlash(regexPattern), error: regexErrorMessage };
            await view.submit({ configuration: config });
        } else {
            setIsFormInvalid(true);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const getContext = async () => {
            const context = await view.getContext();
            setConfigRelatedData(context);
        };
        void getContext();
    }, []);

    return (
        <LicenseBanner isLicenseValid={isLicenseActive}>
            <Wrapper>
                {isLoading && (
                    <PositionedElement position={ABSOLUTE}>
                        <Spinner />
                    </PositionedElement>
                )}
                <FieldConfigContent isLicenseValid={isLicenseActive}>
                    <ContentWrapper>
                        <div>
                            <Label
                                isRequired
                                bottomMargin={4}
                            >
                                Regex pattern
                            </Label>
                            <PatternSection>
                                <Textfield
                                    value={regexPattern}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        setIsFormInvalid(false);
                                        setRegexPattern(e.target.value);
                                    }}
                                    placeholder='Enter pattern or load template'
                                    testId='regex-pattern'
                                    pattern={regexPattern}
                                />
                                <LoadTemplate
                                    onRegexTemplateChange={handleRegexTemplateChange}
                                    selectedTemplate={selectedTemplate}
                                />
                            </PatternSection>
                            {isFormInvalid && <ErrorMessage>This field cannot be empty.</ErrorMessage>}
                            <InfoText topMargin={4}>
                                Read more about regular expression syntax{' '}
                                <a onClick={() => router.open(REGEX_CHEATSHEET_URL)}>in this cheatsheet</a>. You can find more
                                examples <a onClick={() => router.open(DOCS_URL)}>in our documentation</a>.
                            </InfoText>
                        </div>
                        <div>
                            <Label bottomMargin={4}>Regex error message</Label>
                            <Textfield
                                value={regexErrorMessage}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRegexErrorMessage(e.target.value)}
                                testId='regex-error-message'
                            />
                            <InfoText topMargin={4}>
                                Users will see this message if input is invalid. If it’s empty, they will see a default text:{' '}
                                <span className='italic'>
                                    Enter a value that will be validated for the configured regular expression: [regex_pattern]
                                </span>
                                .
                            </InfoText>
                        </div>
                        <TestingArea regexToTest={regexPattern} />
                    </ContentWrapper>
                </FieldConfigContent>
                <Footer
                    hasFixedContent
                    showTopSeparator
                    styling={{
                        position: 'absolute',
                        bottom: 0,
                    }}
                    fixedContentProps={{
                        linkOpener: () => router.open(DOCS_URL),
                        isSubmitting: isLoading,
                        onCancel: () => handleCancel(),
                        onSubmit: () => handleSubmit(),
                        cancelButtonText: 'Cancel',
                        isSubmitDisabled: isFormInvalid,
                        submitButtonText: 'Save field',
                    }}
                />
            </Wrapper>
        </LicenseBanner>
    );
};

export default Configuration;
